import { CheckoutIntentItem, CheckoutIntentLineItem, columns } from './column';
import { capitalCase } from 'change-case';
import { useEffect, useMemo, useState } from 'react';
import { FacetOptions } from 'components/ui/data-table-toolbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { DataTable } from 'components/ui/data-table';
import { Skeleton } from 'components/ui/skeleton';
import { EmptyState } from 'components/EmptyState';
import { Container } from './styles';
import { DataTablePaginationState } from 'components/ui/data-table-pagination';
import { ColumnFiltersState } from '@tanstack/react-table';
import { ActivityDetailSheet } from './components/ActivityDetailSheet';
import { ReactComponent as EmptyBox } from 'assets/icons/empty-box.svg';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Routes } from 'config/constants';
import { CheckoutIntents } from 'checkout-intents';
import * as Sentry from '@sentry/react';
import {
  fetchCheckoutIntentByOrderId,
  useCheckoutIntent,
  useCheckoutIntents,
} from './hooks/useCheckoutIntents';
import { toast } from 'sonner';

const CHECKOUT_INTENT_STATES = [
  { label: 'Completed', value: 'completed' },
  { label: 'Failed', value: 'failed' },
  { label: 'Placing Order', value: 'placing_order' },
  { label: 'Retrieving Offer', value: 'retrieving_offer' },
  { label: 'Awaiting Confirmation', value: 'awaiting_confirmation' },
];

const Page = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CheckoutIntentItem | undefined>();
  const [pageSize, setPageSize] = useState(10);
  const location = useLocation();
  const [deepLinkId, setDeepLinkId] = useState<string | undefined>();
  const [listParams, setListParams] = useState<CheckoutIntents.CheckoutIntentListParams>({
    limit: pageSize,
  });

  const { checkoutIntents, pageInfo, loading: queryLoading } = useCheckoutIntents(listParams);

  const tableData = useMemo(() => checkoutIntents.map(transformCheckoutIntent), [checkoutIntents]);

  useEffect(() => {
    if (location.state?.checkoutIntentId) {
      setDeepLinkId(location.state.checkoutIntentId);
      location.state.checkoutIntentId = undefined;
    }
  }, [location.state?.checkoutIntentId]);

  const {
    checkoutIntent: deepLinkedIntent,
    loading: _deepLinkLoading,
    error: deepLinkError,
  } = useCheckoutIntent(id);

  useEffect(() => {
    if (deepLinkedIntent) {
      setSelectedItem(transformCheckoutIntent(deepLinkedIntent));
      setOpen(true);
    }
  }, [deepLinkedIntent]);

  useEffect(() => {
    if (deepLinkError) {
      toast.error('No checkout intent found for the provided ID');
      navigate(Routes.CheckoutIntents);
    }
  }, [deepLinkError, navigate]);

  // State is the only filter we surface; everything else collapses into
  // the global search (marketplace order-ID lookup).
  const facetOptions = useMemo<FacetOptions>(
    () => ({ state: { options: CHECKOUT_INTENT_STATES } }),
    [],
  );

  const handlePaginationChange = ({
    pageSize: newPageSize,
    direction,
  }: DataTablePaginationState) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }

    setListParams((prev) => ({
      ...prev,
      limit: newPageSize,
      after: direction === 'next' ? pageInfo?.endCursor ?? undefined : undefined,
      before: direction === 'previous' ? pageInfo?.startCursor ?? undefined : undefined,
    }));
  };

  const handleColumnFiltersChange = (filters: ColumnFiltersState) => {
    const sdkFilters: CheckoutIntents.CheckoutIntentListParams = {
      limit: pageSize,
    };

    for (const filter of filters) {
      if (filter.id === 'state') {
        sdkFilters.state = filter.value as CheckoutIntents.CheckoutIntentListParams['state'];
      }
    }

    setListParams(sdkFilters);
  };

  const handleSearch = async (query: string) => {
    const trimmed = query.trim();
    if (trimmed.startsWith('ci_')) {
      navigate(`${Routes.CheckoutIntents}/${trimmed}`);
      return;
    }
    if (trimmed) {
      const checkoutIntent = await fetchCheckoutIntentByOrderId(trimmed).catch((e) => {
        console.error('Error fetching checkout intents by orderId:', e);
        Sentry.captureException(
          new Error('Error fetching checkout intents by orderId', { cause: e }),
        );
      });
      if (!checkoutIntent) {
        toast.error('No checkout intent found for the provided marketplace order ID');
        return;
      }
      navigate(`${Routes.CheckoutIntents}/${checkoutIntent.id}`);
    }
  };

  // Selecting a row pushes the intent ID onto the URL so the sheet state is
  // shareable / refreshable. The deep-link effect above picks the new id up
  // and reuses the row payload we already have, so we don't refetch.
  const handleRowClick = (row: CheckoutIntentItem) => {
    setSelectedItem(row);
    setOpen(true);
    navigate(`${Routes.CheckoutIntents}/${row.id}`);
  };

  const handleSheetOpen = (open: boolean) => {
    setOpen(open);
    if (!open && id) {
      navigate(Routes.CheckoutIntents);
    }
  };

  return (
    <Container>
      <ActivityDetailSheet open={isOpen} onOpenChange={handleSheetOpen} item={selectedItem} />
      <Card className="mt-[28px]">
        <CardHeader className="flex-auto flex-row justify-between">
          <div className="space-y-1">
            {queryLoading ? (
              <Skeleton className="h-4 w-[24px]" />
            ) : (
              <CardTitle className="whitespace-break-spaces">Checkout Intents</CardTitle>
            )}
            <CardDescription>
              List of the checkout intents (orders) created by shoppers
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            loading={queryLoading}
            columns={columns}
            data={tableData}
            onRowClicked={handleRowClick}
            pageSize={pageSize}
            onPaginationChange={handlePaginationChange}
            initialSelectedFacetValues={
              deepLinkId ? [{ values: [deepLinkId], index: 0 }] : undefined
            }
            showSkipToFirstPageButton={true}
            facetOptions={facetOptions}
            hiddenFacets={[
              'id',
              'subtotal',
              'quantity',
              'createdAt',
              'buyerName',
              'buyerEmail',
              'productUrl',
            ]}
            onColumnFiltersChange={handleColumnFiltersChange}
            onSearch={handleSearch}
            searchPlaceholder="Search by marketplace order ID..."
            emptyState={<EmptyState icon={<EmptyBox />} title="No Checkout Intents" />}
            manualPagination={true}
            hideTotalPageCount={true}
            hasNextPage={pageInfo?.hasNextPage ?? false}
            hasPreviousPage={pageInfo?.hasPreviousPage ?? false}
          />
        </CardContent>
      </Card>
    </Container>
  );
};

// The SDK type does not yet declare `items` on BaseCheckoutIntent, but the API
// response carries it for intents created via the items[] payload. Narrow the
// shape here so consumers don't need `as unknown as` casts at each call site.
type IntentWithItems = CheckoutIntents.CheckoutIntent & {
  items?: CheckoutIntentLineItem[];
};

const deriveLineItems = (intent: IntentWithItems): CheckoutIntentLineItem[] => {
  if (intent.items && intent.items.length > 0) {
    return intent.items;
  }
  // Legacy single-item intents only carry top-level productUrl/quantity;
  // synthesize a one-element array so downstream code has a single source of truth.
  return [{ productUrl: intent.productUrl, quantity: intent.quantity }];
};

const transformCheckoutIntent = (intent: IntentWithItems): CheckoutIntentItem => {
  let deliveryEstimate: string | undefined | null;

  if (intent.state !== 'failed' && intent.state !== 'retrieving_offer') {
    deliveryEstimate = intent.offer.shipping.availableOptions.find(
      (option) => option.id === intent.offer.shipping.selectedOptionId,
    )?.deliveryEstimate?.latest;
  }

  return {
    id: intent.id,
    state: capitalCase(intent.state),
    stateRaw: intent.state,
    createdAt: new Date(intent.createdAt).toISOString(),
    productUrl: intent.productUrl,
    quantity: intent.quantity,
    items: deriveLineItems(intent),
    buyerEmail: intent.buyer.email,
    buyerFirstName: intent.buyer.firstName,
    buyerLastName: intent.buyer.lastName,
    buyerName: `${intent.buyer.firstName} ${intent.buyer.lastName}`.trim(),
    buyer: intent.buyer,
    cost:
      intent.state !== 'failed' && intent.state !== 'retrieving_offer' && intent.offer
        ? {
            totalCents: intent.offer.cost.total.amountSubunits,
            subtotalCents: intent.offer.cost.subtotal.amountSubunits,
            shippingCents: intent.offer.cost.shipping?.amountSubunits ?? 0,
            taxCents: intent.offer.cost.tax?.amountSubunits ?? 0,
            discountCents: intent.offer.cost.discount?.amountSubunits ?? 0,
            surchargeCents: intent.offer.cost.surcharge?.amountSubunits ?? 0,
            currency: intent.offer.cost.total.currencyCode,
          }
        : {
            totalCents: 0,
            subtotalCents: 0,
            shippingCents: 0,
            taxCents: 0,
            discountCents: 0,
            surchargeCents: 0,
            currency: 'USD',
          },
    orderIds: intent.state === 'completed' && intent.orderId ? [intent.orderId] : undefined,
    failReason: intent.state === 'failed' ? intent.failureReason.message : undefined,
    failureCode: intent.state === 'failed' ? intent.failureReason.code : undefined,
    estimatedDeliveryDate: deliveryEstimate ? new Date(deliveryEstimate) : undefined,
  };
};

export default Page;
